import { useEffect, useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Person } from "../../models/person";
import { useToaster } from "../../components/hooks/useToaster";
import { Tooltip } from "react-tooltip";
import { useLocalStorageSync } from "../../components/hooks/useLocalStorageSync.tsx";
import { useDispatch, useSelector } from "react-redux";
import {
  updateProfilePicture,
  updateNewProfilePicture,
  removeProfilePicture,
  updateProfile,
  updatePassword,
  fetchProfileData,
  profilePicture,
  updateProfileField,
  sendPasswordResetEmail,
} from "../../reducers/sessionSlice";
import type { AppDispatch } from "../../reducers/store";
import { RootState } from "../../reducers/store";
import { saveAttachment } from "../../reducers/attachmentSlice.ts";
import { getProfilePicture } from "../../reducers/profileSlice.ts";
import { getProfileData } from "../../reducers/profileSlice";

const passwordVisibleIcons = {
  hidden: (
    <svg
      className="size-4 shrink-0 text-gray-400"
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      viewBox="0 0 16 16"
    >
      <path
        className="hs-password-active:hidden"
        d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"
      />
      <path
        className="hs-password-active:hidden"
        d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"
      />
      <path
        className="hs-password-active:hidden"
        d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"
      />
      <path
        className="hs-password-active:block hidden"
        d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"
      />
      <path
        className="hs-password-active:block hidden"
        d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"
      />
    </svg>
  ),
  visible: (
    <svg
      className="size-4 shrink-0 text-gray-400"
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      viewBox="0 0 16 16"
    >
      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"></path>
      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"></path>
    </svg>
  ),
};

interface ProfileSettingsProps {
  pfp: string;
  setProfilePicture: (s: string) => void;
}

export function ProfileSettings(props: ProfileSettingsProps): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [toasterState, openToaster, closeToaster] = useToaster();
  const [profilePictureUploadInProgress, setProfilePictureUploadInProgress] =
    useState(false);
  const [syncCounter, refreshLocalStorage] = useLocalStorageSync();
  const [isLoading, setIsLoading] = useState(true);

  // Get profile data from Redux
  const { profileData, profile } = useSelector(
    (state: RootState) => state.session,
  );

  const profilePicture = useSelector(
    (state: RootState) => state.session.profilePicture,
  );

  // Initialize form state with empty strings
  const [formState, setFormState] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    title: "",
  });

  // Update form state when profileData changes
  useEffect(() => {
    if (profileData) {
      setFormState({
        firstName: profileData.firstName || "",
        lastName: profileData.lastName || "",
        phone: profileData.phone || "",
        title: profileData.title || "",
      });
      setIsLoading(false);
    }
  }, [profileData]);

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        setTimeout(async () => {
          const result = await dispatch(fetchProfileData());
          if (result.type.endsWith('/rejected')) {
            console.error('Failed to fetch profile data:', result.payload);
          }
        }, 1000);
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };
  
    fetchData();
  }, [dispatch]);

  const onComplete = useCallback(() => {
    console.log("File upload completed");
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: any) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      try {
        setProfilePictureUploadInProgress(true);
        const saveResult = await dispatch(saveAttachment({
          attachment: { filename: file.name } as any,
          uploadId: null,
          onComplete,
          file,
        }));

        if (saveResult.type.endsWith('/rejected')) {
          throw new Error('Failed to save attachment');
        }

        const id = (saveResult.payload as any).id;
        const updateResult = await dispatch(updateProfilePicture({ attachmentId: id, file }));
        
        if (updateResult.type.endsWith('/rejected')) {
          throw new Error('Failed to update profile picture');
        }

        const pictureResult = await dispatch(getProfilePicture());
        if (pictureResult.type.endsWith('/rejected')) {
          throw new Error('Failed to get profile picture');
        }

        props.setProfilePicture(pictureResult.payload as string);
      } catch (error) {
        openToaster("Failed to update profile picture", "medium", "error");
      } finally {
        setProfilePictureUploadInProgress(false);
      }
    },
    [dispatch],
  );

  const onRemoveButtonClicked = async () => {
    try {
      const result = await dispatch(removeProfilePicture());
      
      if (result.type.endsWith('/rejected')) {
        throw new Error('Failed to remove profile picture');
      }

      props.setProfilePicture("");
      refreshLocalStorage();
    } catch (error) {
      openToaster("Failed to remove profile picture", "medium", "error");
    }
  };

  const validatePassword = (password: string): boolean => {
    const regex =
      /^(?=.*[A-Za-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
    const isValid = regex.test(password);

    if (!isValid) {
      openToaster(
        "Password does not meet requirements of at least 8 characters, contains a number, contains a special character. Please try again.",
        "medium",
        "error",
      );
    }

    return isValid;
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const phonePattern = /[0-9]{3}[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/;
    return phonePattern.test(phone);
  };

  const handlePasswordError = (error: any) => {
    // The error data is directly in the response data
    const errorData = error.response?.data;

    if (errorData?.code && errorData?.message) {
      // Use the server's message directly
      openToaster(errorData.message, "medium", "error");
    } else {
      openToaster("An error occurred. Please try again.", "medium", "error");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let passwordUpdated = false;

      // Check if new password fields are filled, otherwise skip password update
      if (newPassword || oldPassword) {
        if (!newPassword) {
          openToaster("Enter new password to continue.", "medium", "error");
          return;
        }

        if (!oldPassword) {
          openToaster("Enter current password to continue.", "medium", "error");
          return;
        }

        try {
          const result = await dispatch(updatePassword({ newPassword, oldPassword }));
          
          if (result.type.endsWith('/rejected')) {
            const error = result.payload as { code: string; message: string };
            throw error;
          }

          openToaster("Password updated successfully", "medium", "success");
          passwordUpdated = true;
        } catch (error: any) {
          // The error structure comes from rejectWithValue payload
          const errorCode = error?.code;
          const errorMessage = error?.message;

          switch (errorCode) {
            case "INCORRECT_OLD_PASSWORD":
              openToaster(
                "Current password is incorrect. Please try again.",
                "medium",
                "error",
              );
              break;
            case "PASSWORDS_MATCH":
              openToaster(
                "New password must be different from the old password.",
                "medium",
                "error",
              );
              break;
            default:
              openToaster(
                errorMessage || "An error occurred. Please try again.",
                "medium",
                "error",
              );
          }
          return;
        }

        // Reset password fields after submission
        setNewPassword("");
        setOldPassword("");
      }

      // Only validate phone number if we're updating profile data
      if (Object.keys(profileData || {}).length > 0) {
        if (formState.phone && !validatePhoneNumber(formState.phone)) {
          openToaster(
            "Please enter a valid phone number in the format 123-456-7890.",
            "medium",
            "error",
          );
          return;
        }

        const profileResult = await dispatch(updateProfile(profileData as Person));
        if (profileResult.type.endsWith('/rejected')) {
          throw new Error('Failed to update profile');
        }
      }

      openToaster(
        `Profile${passwordUpdated ? " and password" : ""} updated successfully`,
        "medium",
        "success",
      );
    } catch (error: any) {
      openToaster("An error occurred. Please try again.", "medium", "error");
    }
  };

  const onForgotPasswordClicked = async () => {
    const email = localStorage.getItem("userEmail");
    if (!email) {
      openToaster("No email found", "medium", "error");
      return;
    }

    try {
      const result = await dispatch(sendPasswordResetEmail(email));
      
      if (result.type.endsWith('/rejected')) {
        throw new Error('Failed to send password reset email');
      }

      openToaster(
        "Password reset link sent to your email",
        "medium",
        "success",
      );
    } catch (error) {
      openToaster("Failed to send password reset email", "medium", "error");
    }
  };

  const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [id]: value,
    }));
    dispatch(updateProfileField({ field: id, value }));
  };

  const onTogglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    multiple: false,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpeg"],
    },
    onDrop,
  });

  const renderPasswordVisibilityIcon = (passwordVisible: boolean) => {
    return passwordVisible
      ? passwordVisibleIcons.visible
      : passwordVisibleIcons.hidden;
  };

  return (
    <div
      className="flex max-h-[100%] w-full flex-col overflow-auto px-4 pb-2 pt-2 sm:px-6 md:h-1/2 md:px-7"
      {...getRootProps()}
    >
      <div className="pb-2">
        <span className="mb-2 text-lg font-bold sm:text-xl md:text-2xl">
          Edit Profile
        </span>
      </div>
      <div>
        <span className="sm:text-md mb-2 text-sm font-bold">Profile Photo</span>
        <div>
          <span className="text-xs text-gray-700 sm:text-sm">
            Add or edit a profile photo
          </span>
        </div>
        <div>
          <div className="mt-2 flex flex-col items-center space-x-0 sm:flex-row sm:space-x-6">
            <div className="mb-3 mt-2">
              {profilePictureUploadInProgress ? (
                <img
                  className="h-16 w-16 rounded-full bg-cover bg-center sm:h-20 sm:w-20 md:h-24 md:w-24"
                  src="/dashboard/loading-sm.gif"
                  alt="Loading Picture"
                />
              ) : profilePicture && profilePicture ? (
                <div
                  className="h-16 w-16 rounded-full bg-cover bg-center sm:h-20 sm:w-20 md:h-24 md:w-24"
                  style={{ backgroundImage: `url(${profilePicture})` }}
                  role="img"
                  aria-label={`profile picture:  ${profilePicture}`}
                />
              ) : (
                <img
                  className="h-16 w-16 rounded-full bg-cover bg-center sm:h-20 sm:w-20 md:h-24 md:w-24"
                  src="/dashboard/avatar.png"
                  alt="Profile Picture"
                />
              )}
            </div>

            <div>
              <div>
                <input {...getInputProps()} accept="image/png, image/jpeg" />
                <div
                  className="sm:text-md w-full cursor-pointer rounded border-0 bg-tqsYellow px-2 py-2 text-xs font-semibold text-tqsBlack hover:bg-gray-300 sm:px-3"
                  onClick={open}
                >
                  <p className="cursor-pointer select-none text-center text-[14px]">
                    Change Photo
                  </p>
                </div>
              </div>
              <div className="mt-3">
                <button
                  className="hover:bounce flex items-center rounded border bg-white px-6 py-2 text-xs font-semibold text-tqsBlack sm:px-8 sm:text-xs"
                  onClick={onRemoveButtonClicked}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="mr-2 h-4 w-4 text-tqsRed sm:h-5 sm:w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                    />
                  </svg>
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
        <div>
          <span className="pt-2 text-xs font-semibold text-gray-400 sm:text-sm">
            Recommended image dimensions: 1080x1080 pixels
          </span>
        </div>
      </div>
      <form className="w-full max-w-lg pt-2" onSubmit={handleSubmit}>
        <div className="-mx-3 mb-2 mt-10 flex flex-wrap">
          <div className="mb-2 w-full px-3 md:mb-0 md:w-1/2">
            <label className="mb-2 block text-xs font-medium text-[#8e8e93] sm:text-sm">
              First Name
            </label>
            <input
              className="mb-3 block w-full appearance-none rounded border bg-white px-4 py-2 text-xs font-semibold leading-tight text-gray-700 focus:bg-white focus:outline-none sm:text-sm"
              id="firstName"
              name="first-name"
              type="text"
              placeholder="Jane"
              value={formState.firstName}
              onChange={onValueChange}
            />
          </div>
          <div className="w-full px-3 md:w-1/2">
            <label className="mb-2 block text-xs font-medium text-[#8e8e93] sm:text-sm">
              Last Name
            </label>
            <input
              className="block w-full appearance-none rounded border bg-white px-4 py-2 text-xs font-semibold leading-tight text-gray-700 focus:bg-white focus:outline-none sm:text-sm"
              id="lastName"
              name="last-name"
              type="text"
              placeholder="Doe"
              value={formState.lastName}
              onChange={onValueChange}
            />
          </div>
        </div>
        <div className="-mx-3 mb-2 flex flex-wrap">
          <div className="mb-6 w-full px-3 md:mb-0 md:w-1/2">
            <label
              className="mb-2 block text-xs font-medium text-[#8e8e93] sm:text-sm"
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Format: 123-456-7890"
            >
              Phone Number
              <Tooltip place="top-start" id="my-tooltip"></Tooltip>
            </label>
            <input
              className="mb-3 block w-full appearance-none rounded border bg-white px-4 py-2 text-xs font-semibold leading-tight text-gray-700 focus:bg-white focus:outline-none sm:text-sm"
              id="phone"
              name="phone-number"
              type="text"
              value={formState.phone}
              onChange={onValueChange}
              placeholder="Phone Number"
            />
          </div>
          <div className="w-full px-3 md:w-1/2">
            <label className="mb-2 block text-xs font-medium text-[#8e8e93] sm:text-sm">
              Title
            </label>
            <input
              className="block w-full appearance-none rounded border bg-white px-4 py-2 text-xs font-semibold leading-tight text-gray-700 focus:bg-white focus:outline-none sm:text-sm"
              id="title"
              name="title"
              type="text"
              value={formState.title}
              onChange={onValueChange}
              placeholder="Title"
            />
          </div>
        </div>
        <hr className="my-3 h-px border-0 bg-gray-200 xs:w-[70%] sm:w-[70%] md:my-3 md:w-[110%] lg:w-[100%] 2xl:absolute 2xl:left-0 2xl:right-0 2xl:mx-auto 2xl:w-[85%]" />
        <div className="pb-2 2xl:pt-6">
          <span className="sm:text-md text-lg font-bold leading-tight">
            Password
          </span>
          <div className="leading-tight">
            <span className="text-xs font-medium text-gray-700 sm:text-sm">
              Update your password
            </span>
          </div>
        </div>

        {/* <label
          className="mb-1 mt-4 block text-xs font-medium text-[#8e8e93] sm:text-sm"
          htmlFor="old-password"
        >
          Old Password
        </label>
        <div className="relative">
          <input
            id="old-password"
            type={passwordVisible ? "text" : "password"}
            name="old-password"
            className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-xs focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-0 sm:w-1/2 sm:text-sm"
            placeholder="Input your old password"
            value={oldPassword} // Bind the state variable here
            onChange={(e) => setOldPassword(e.target.value)} // Update the state on input change
          />
          <button
            type="button"
            onClick={() => onTogglePasswordVisibility()}
            className="absolute inset-y-0 end-0 z-10 flex cursor-pointer items-center p-3.5 sm:left-52"
          >
            {renderPasswordVisibilityIcon(passwordVisible)}
          </button>
        </div>

        <label
          className="mb-1 block pt-4 text-xs font-medium text-[#8e8e93] sm:text-sm"
          htmlFor="new-password"
        >
          New Password
        </label>
        <div className="relative">
          <input
            id="new-password"
            type={passwordVisible ? "text" : "password"}
            name="new-password"
            className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-xs focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-0 sm:w-1/2 sm:text-sm"
            placeholder="Input your new password"
            value={newPassword} // Bind the state variable here
            onChange={(e) => setNewPassword(e.target.value)} // Update the state on input change
          />
          <button
            type="button"
            onClick={() => onTogglePasswordVisibility()}
            className="absolute inset-y-0 end-0 z-10 flex cursor-pointer items-center p-3.5 sm:left-52"
          >
            {renderPasswordVisibilityIcon(passwordVisible)}
          </button>
        </div> */}
        <div className="pt-5">
          {/* <input
            className="cursor-pointer rounded border border-tqsYellow bg-tqsYellow px-4 py-2 text-xs font-semibold text-tqsBlack sm:text-sm"
            type="submit"
            value="Save Changes"
          /> */}
          <span
            className="cursor-pointer rounded border border-tqsYellow bg-tqsYellow px-4 py-2 text-xs font-semibold text-tqsBlack sm:text-sm"
            onClick={onForgotPasswordClicked}
          >
            Reset Password
          </span>
        </div>
      </form>
    </div>
  );
}
